import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ClipboardList } from "lucide-react";
import { Reveal, Eyebrow } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#EADFC6] bg-white focus:border-[#A6823B] focus:ring-4 focus:ring-[#A6823B]/10 outline-none text-[#2A2521] transition-all";

const EMPTY = {
  name: "", phone: "", email: "", birth_date: "",
  conditions: "", allergies: "", medications: "", pregnancy: "", smoker: "", reason: "",
};

const Radio = ({ label, name, value, onChange, options, testId }) => (
  <div data-testid={testId}>
    <label className="text-sm font-medium text-[#4A3F2E] mb-2 block">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          onClick={() => onChange(o)}
          data-testid={`${testId}-${o.toLowerCase().replace(/\s+/g, "-")}`}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            value === o
              ? "bg-[#A6823B] text-white shadow-md shadow-[#A6823B]/20"
              : "bg-white border border-[#EADFC6] text-[#4A3F2E] hover:border-[#A6823B]/50"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const ChestionarPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!gdpr) {
      toast.error("Te rugăm să accepți prelucrarea datelor (GDPR).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/questionnaires`, form);
      toast.success("Chestionar trimis! Mulțumim — îl citim înainte de consultație.");
      setForm(EMPTY);
      setGdpr(false);
    } catch {
      toast.error("A apărut o eroare. Încearcă din nou sau completează chestionarul la clinică.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-[76px] md:pt-[112px]" data-testid="chestionar-page">
      <section className="py-16 lg:py-20 bg-[#FBF7EF] medical-grid">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Chestionar pacient</Eyebrow>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-[#2A2521] mt-3">
              Spune-ne câteva lucruri despre tine
            </h1>
            <p className="text-[#6B5C43] mt-4 text-base md:text-lg leading-relaxed">
              Completează chestionarul înainte de prima vizită — medicul tău îl citește în avans,
              iar consultația începe direct cu ce contează pentru tine.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="chestionar-form"
              className="mt-10 bg-white rounded-2xl border border-[#EADFC6] p-6 lg:p-8 space-y-5 shadow-lg shadow-[#2A2521]/5"
            >
              <div className="flex items-center gap-3 pb-2 border-b border-[#F0E9D8]">
                <span className="w-10 h-10 rounded-xl bg-[#EFE7D6] flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-[#A6823B]" />
                </span>
                <h2 className="font-display font-bold text-[#2A2521]">Date de contact</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Nume complet *</label>
                  <input required value={form.name} onChange={set("name")} data-testid="chestionar-name" className={inputCls} placeholder="Ion Popescu" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Telefon *</label>
                  <input required value={form.phone} onChange={set("phone")} data-testid="chestionar-phone" className={inputCls} placeholder="07xx xxx xxx" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Email</label>
                  <input type="email" value={form.email} onChange={set("email")} data-testid="chestionar-email" className={inputCls} placeholder="email@exemplu.ro" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Data nașterii</label>
                  <input type="date" value={form.birth_date} onChange={set("birth_date")} data-testid="chestionar-birth-date" className={inputCls} />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 pb-2 border-b border-[#F0E9D8]">
                <h2 className="font-display font-bold text-[#2A2521]">Istoric medical</h2>
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Afecțiuni medicale cunoscute (diabet, boli cardiace, hipertensiune etc.)</label>
                <input value={form.conditions} onChange={set("conditions")} data-testid="chestionar-conditions" className={inputCls} placeholder="Ex: diabet, hipertensiune — sau lasă gol" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Alergii la medicamente sau anestezice</label>
                <input value={form.allergies} onChange={set("allergies")} data-testid="chestionar-allergies" className={inputCls} placeholder="Ex: penicilină — sau lasă gol" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Medicamente pe care le iei în prezent</label>
                <input value={form.medications} onChange={set("medications")} data-testid="chestionar-medications" className={inputCls} placeholder="Ex: anticoagulante — sau lasă gol" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Radio label="Ești însărcinată sau alăptezi?" name="pregnancy" value={form.pregnancy} onChange={setVal("pregnancy")} options={["Da", "Nu", "Nu se aplică"]} testId="chestionar-pregnancy" />
                <Radio label="Fumezi?" name="smoker" value={form.smoker} onChange={setVal("smoker")} options={["Da", "Nu", "Ocazional"]} testId="chestionar-smoker" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Motivul vizitei</label>
                <textarea rows={3} value={form.reason} onChange={set("reason")} data-testid="chestionar-reason" className={inputCls} placeholder="Ce te deranjează sau ce ți-ai dori să schimbi?" />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={gdpr}
                  onChange={(e) => setGdpr(e.target.checked)}
                  data-testid="chestionar-gdpr-checkbox"
                  className="mt-1 w-4 h-4 accent-[#A6823B]"
                />
                <span className="text-sm text-[#8A7A5E]">
                  Sunt de acord cu prelucrarea datelor mele, inclusiv a datelor medicale, în scopul consultației, conform GDPR. *
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="chestionar-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] disabled:opacity-60 text-white font-semibold px-5 py-3.5 rounded-full transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Trimite chestionarul
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ChestionarPage;
